import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { fetchFreshness } from "@/lib/api/admin";
import type { DataSourceFreshness } from "@/lib/api/admin";
import { ingestDataSource, listDataSources } from "@/lib/api/dataSources";
import type { DataSource } from "@/lib/api/dataSources";

export function DataSourcesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const sourcesQuery = useQuery<DataSource[], Error>({
    queryKey: ["data-sources"],
    queryFn: listDataSources,
  });
  const freshnessQuery = useQuery<DataSourceFreshness[], Error>({
    queryKey: ["data-sources", "freshness"],
    queryFn: fetchFreshness,
    refetchInterval: 30_000,
  });

  const ingestMutation = useMutation({
    mutationFn: (id: string) => ingestDataSource(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["data-sources"] });
      void queryClient.invalidateQueries({ queryKey: ["data-sources", "freshness"] });
    },
  });

  const freshnessById = new Map<string, DataSourceFreshness>();
  for (const f of freshnessQuery.data ?? []) {
    freshnessById.set(f.data_source_id, f);
  }

  const isLoading = sourcesQuery.isLoading;
  const isError = sourcesQuery.isError;
  const sources = sourcesQuery.data ?? [];

  return (
    <div className="flex-1 overflow-auto">
      {/* Page header */}
      <div className="border-b border-pantera-100 bg-white px-10 py-7">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow">Configuración</p>
            <h1 className="mt-1.5 text-3xl font-black tracking-tight text-pantera-900">
              {t("dataSources.title")}
            </h1>
            <p className="mt-1 text-[13px] text-muted">{t("dataSources.subtitle")}</p>
          </div>
          <div className="flex gap-2 pt-1">
            <button className="btn-ghost text-sm">Sincronizar todo</button>
            <button className="btn-primary rounded-pill px-4 py-2 text-sm">
              + Conectar nueva fuente
            </button>
          </div>
        </div>
      </div>

      <div className="px-10 py-7">
        {isLoading && <p className="text-sm text-muted">{t("dataSources.loading")}</p>}

        {isError && (
          <div className="rounded-card border border-error/20 bg-error/5 p-3 text-sm text-error">
            {t("dataSources.error", { message: sourcesQuery.error?.message ?? "" })}
          </div>
        )}

        {!isLoading && sources.length === 0 && (
          <p className="text-sm text-muted">{t("dataSources.empty")}</p>
        )}

        {sources.length > 0 && (
          <div className="card overflow-hidden">
            <table className="w-full table-auto border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-pantera-100 bg-[#f5f0ff]">
                  {[
                    t("dataSources.columns.name"),
                    t("dataSources.columns.kind"),
                    t("dataSources.columns.family"),
                    t("dataSources.columns.host"),
                    t("dataSources.columns.enabled"),
                    t("dataSources.columns.freshness"),
                    t("dataSources.columns.actions"),
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-3.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-pantera-700 ${
                        i === 6 ? "text-right" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => {
                  const freshness = freshnessById.get(source.id);
                  const enabled = source.enabled;
                  return (
                    <tr
                      key={source.id}
                      className="border-b border-pantera-100 transition-colors last:border-b-0 hover:bg-[#fffbf2]"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] ${
                              enabled
                                ? "bg-pantera-100 text-pantera-700"
                                : "bg-slate-100 text-slate-400"
                            }`}
                          >
                            <DatabaseIcon />
                          </div>
                          <div>
                            <div className="font-semibold text-pantera-900">{source.name}</div>
                            <div className="font-mono text-[10px] text-muted">
                              id: {source.id.slice(0, 8)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[13px] text-muted">{source.kind}</td>
                      <td className="px-5 py-4 text-[13px] text-muted">{source.family}</td>
                      <td className="px-5 py-4 text-[13px] text-muted">
                        {source.host ?? "—"}
                        {source.port ? `:${source.port}` : ""}
                      </td>
                      <td className="px-5 py-4">
                        {enabled ? (
                          <span className="badge bg-pantera-700 text-white">
                            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                            {t("dataSources.statusEnabled")}
                          </span>
                        ) : (
                          <span className="badge bg-slate-200 text-slate-600">
                            {t("dataSources.statusDisabled")}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <FreshnessBadge freshness={freshness} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => ingestMutation.mutate(source.id)}
                          disabled={ingestMutation.isPending}
                          className={`rounded-pill px-4 py-1.5 text-xs font-bold transition-colors ${
                            enabled
                              ? "bg-accent-500 text-pantera-900 hover:bg-accent-600 disabled:opacity-50"
                              : "bg-pantera-700 text-white hover:bg-pantera-900 disabled:opacity-50"
                          }`}
                        >
                          {ingestMutation.isPending && ingestMutation.variables === source.id
                            ? t("dataSources.actions.running")
                            : enabled
                              ? t("dataSources.actions.ingest")
                              : "Conectar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {ingestMutation.isError && (
          <div className="mt-4 rounded-card border border-error/20 bg-error/5 p-3 text-sm text-error">
            {t("dataSources.error", {
              message: (ingestMutation.error as Error)?.message ?? "",
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FreshnessBadge({ freshness }: { freshness: DataSourceFreshness | undefined }) {
  const { t } = useTranslation();
  if (!freshness || !freshness.last_ingest_at) {
    return <span className="text-xs text-muted/60">{t("dataSources.freshness.never")}</span>;
  }
  const minutes = freshness.minutes_since_last_ingest ?? 0;
  const tone =
    minutes < 60
      ? "bg-success/10 text-success"
      : minutes < 1440
        ? "bg-accent-400/30 text-accent-600"
        : "bg-error/10 text-error";
  const label =
    minutes < 60
      ? t("dataSources.freshness.minutes", { count: minutes })
      : minutes < 1440
        ? t("dataSources.freshness.hours", { count: Math.round(minutes / 60) })
        : t("dataSources.freshness.days", { count: Math.round(minutes / 1440) });

  return (
    <span className={`badge ${tone} text-[11px]`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {label} · {freshness.rows_in_canonical.toLocaleString()} rows
    </span>
  );
}

function DatabaseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
      <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
  );
}
